/**
 * כרטיס החברה — the client's MASTER RECORD. `/tenant/companies/{company_uid}`.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS SCREEN EXISTS, GIVEN THAT `/c/{company_uid}` ALREADY DOES
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A screen was not added to make a route reachable. It was added because two different questions
 * about a company were sharing one address and one of them had no answer at all.
 *
 *   `/c/{company_uid}`              THE WORK SURFACE. Modules, periods, documents. What a bookkeeper
 *                                   opens to do this month's work. It is entered through the context
 *                                   bar and it carries a PERIOD.
 *   `/tenant/companies/{uid}`       THE ADMINISTRATIVE RECORD. The company's legal name, its
 *                                   lifecycle state, its external identifiers, its source bindings,
 *                                   its portfolio. What an office manager opens when a client is
 *                                   renamed, goes dormant, merges, or stops being a client.
 *
 * They are different jobs, held by different capabilities (`companies.company.read` against
 * `companies.company.manage`), reached from different parents, and the second one had NOWHERE TO
 * LIVE: `companies.company.update` and `companies.company.lifecycle` were server routes with no
 * browser path, and `companies.identifier.read` and `companies.portfolio.assign` had none either.
 * Hanging an editing form off the work surface would have put "mark this client as former" one click
 * from "open the trial balance", which is the arrangement that produces the accident.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CARD WAS A STACK, AND IT IS NOW A HEADER, TABS AND GROUPS. **2026-10-25.**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The Owner, after opening it for the first time on a company he had actually created:
 *
 *     *"ארגן את הגדרות הארגון והחברה במבנה ברור בהשראת הצילומים ששלחת … בתוך כרטיס החברה כותרת קבועה
 *     עם שם ומספר רשמי, טאבים לפי נושא וקבוצות שדות מתקפלות … והצג רצף ברור: פרטי חברה → מערכת
 *     הנהלת חשבונות → חיבור → הרשאות והשלמת הפעלה. אפשר לשמור ולהמשיך בהמשך."*
 *
 * The first four tabs are that sequence, in that order. **The fifth is deliberately not in it:**
 * moving a client to dormant or to former is not a step in activating one, and putting it at the
 * end of an activation flow is how it gets clicked by somebody working through the steps. It keeps
 * the separation the top of this file argues for, one level further in.
 *
 * What stays OUTSIDE the tabs: the record's identity, and anything that reports a problem with the
 * record. A warning that only appears when you happen to open the right tab is a warning the
 * product cannot claim it gave.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE LIFECYCLE IS A TRANSITION, NEVER A FIELD
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The server models it as a POST to `/lifecycle` naming a MOVE, deliberately: *"a client cannot
 * express 'set lifecycle_status' at all — it can only ask for a move."* So this screen offers verbs
 * — `העברה לתרדמת`, `סימון כלקוח לשעבר` — and never a dropdown of states. And when a move is refused
 * the server returns where the company actually is plus the moves that WOULD work, which is what
 * turns a refusal into the next three buttons instead of a sentence.
 *
 * `merged_into` is the one move that cannot be expressed without a second company, and the server's
 * input union cannot be constructed without one. The form therefore asks, rather than sending a merge
 * with no target and reading the 400 back.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * AND THE LIFECYCLE STATE IS DISPLAYED, NEVER INFERRED
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 09 decision 10, measured: `company_tags.json` carried `active=true` for 8 companies, `active=false`
 * for 138, and NO `active` key at all for 167 — the largest group — while two code paths resolved
 * that absence in opposite directions. Every state on this screen is the one the server stated.
 */

import { useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent, ReactNode } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { CompanyAccountingSystem } from '../components/CompanyAccountingSystem';
import { CompanySettingsPanel } from '../components/CompanySettingsPanel';
import { FactList } from '../components/FactList';
import { FieldGroup } from '../components/FieldGroup';
import { LifecycleChip } from '../components/LifecycleChip';
import { RecordHeader } from '../components/RecordHeader';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { CompanyWorkItems } from '../components/CompanyWorkItems';
import { TabStrip } from '../components/TabStrip';
import type { TabDefinition } from '../components/TabStrip';
import type { CompanyLifecycleStatus, CompanySummary } from '../api/contract';
import { COMPANY_LIFECYCLE_STATUSES } from '../api/contract';
import { lifecycleStatusHe } from '../api/adminVocabulary';
import {
  companyTransitionRefusalText,
  companyTransitionVerbHe,
  transitionNeedsMergeTarget,
} from '../api/lifecycleText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureExternalCompanies, fixturesEnabled } from '../api/fixtures';
import { CompanyReminderSettings } from '../components/CompanyReminderSettings';
import { routes } from '../routes';

export interface TenantCompanyScreenProps {
  readonly companyUid: string;
}


type Outcome =
  | { readonly kind: 'updated' }
  | { readonly kind: 'moved'; readonly to: CompanyLifecycleStatus }
  | { readonly kind: 'portfolioAssigned' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function knownStatuses(values: readonly string[] | undefined): readonly CompanyLifecycleStatus[] {
  const known: readonly string[] = COMPANY_LIFECYCLE_STATUSES;
  return (values ?? []).filter((v): v is CompanyLifecycleStatus => known.includes(v));
}

export function TenantCompanyScreen({ companyUid }: TenantCompanyScreenProps): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [legalName, setLegalName] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [entityKind, setEntityKind] = useState<string | null>(null);
  const [taxId, setTaxId] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState<CompanyLifecycleStatus | null>(null);
  const [mergeTargetUid, setMergeTargetUid] = useState('');
  // THE ROUTE TAKES A PARTNER AND A KIND, NOT A CODE.
  //
  // This screen used to hold a single `portfolioCode` text field and post `{portfolioCode}`.
  // `companies.portfolio.assign` reads `membershipUid` and `kind` and answers 400 to anything else,
  // so the control could never succeed — G-01's shape exactly: the right URL, a body the handler
  // does not read, and a generic refusal that looks like the operation simply failing. The declared
  // request shape in `api/operations.ts` is what turned it into a compile error.
  const [portfolioMembershipUid, setPortfolioMembershipUid] = useState('');
  const [portfolioKind, setPortfolioKind] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('card.title')} requires="TENANT" actual={ctx.surface} />;
  }

  const company: CompanySummary | null =
    ctx.visibleCompanies.find((c) => c.companyUid === companyUid) ?? null;
  /*
   * THE REGISTER'S OWN NAME. **Section 1, 2026-10-27.** `nav.companies` is *"מעבר לחברה"* -- the
   * PICKER in the rail, the grant-filtered list of companies a person may OPEN. This link goes to
   * the office REGISTER, which the product already calls `חברות`, and the area bar's tab now says
   * so too. The old label produced *"חזרה למעבר לחברה"* on the button's accessible name.
   */
  const back = { labelHe: t('settings.e.companies'), href: routes.tenantCompanies };

  if (company === null) {
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={t('card.title')} backToParent={back}>
        {/* Careful not to confirm or deny that a company with this uid exists: that is the gate's rule. */}
        <StateBlock
          titleHe={t('card.unavailableTitle')}
          bodyHe={t('card.unavailableBody')}
          tone="unavailable"
          nextStep={
            <a className="btn btn--nav" href={back.href}>
              {t('card.backToList')}
            </a>
          }
        />
      </AppShell>
    );
  }

  const bindings = (fixturesEnabled() ? fixtureExternalCompanies : []).filter(
    (e) => e.boundCompanyUid === companyUid,
  );

  async function run(action: () => Promise<Outcome>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      setOutcome(await action());
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  /** Renders the company's own conflict vocabulary — and offers the moves the server said would work. */
  function renderCompanyConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const text = companyTransitionRefusalText(
      {
        reason: failure.body.error,
        from: knownStatuses(failure.body.from === undefined ? [] : [failure.body.from])[0] ?? null,
        allowedTransitions: knownStatuses(failure.body.allowedTransitions),
      },
      lifecycleStatusHe,
    );
    return (
      <section className="denial" data-tone="blocked" role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__explanation">{text.unchangedHe}</p>
        <p className="denial__next-step">{text.nextStepHe}</p>
        {/*
          The actionable half made clickable. These are the moves FROM WHERE THE COMPANY ACTUALLY IS,
          which is why they can differ from what the form offered a moment ago — that list was built
          before the refusal, and the refusal is what proved it stale.
        */}
        {text.allowedTransitions.length === 0 ? null : (
          <div className="actions-row">
            {text.allowedTransitions.map((status) => (
              <button
                key={status}
                type="button"
                className="btn"
                onClick={() => setMoveTo(status)}
              >
                {companyTransitionVerbHe(status)}
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  function saveDetails(e: FormEvent, current: CompanySummary): void {
    e.preventDefault();
    void run(async () => {
      // The company is in the ADDRESS. `CompanyEditInput` declares `tenantId?: never` and
      // `lifecycleStatus?: never`, so neither can travel with an edit even by accident.
      await callOperation('companies.company.update', {
        params: { company_uid: companyUid },
        body: {
          legalName: legalName ?? current.legalName,
          // The company's OWN display name, not its legal name. This sent `current.legalName`, so
          // saving any other field on this card overwrote the name the office had given the
          // company with its legal name — silently, and on every save.
          displayName: displayName ?? current.displayName,
          entityKind: entityKind ?? current.entityKind,
          taxId: taxId ?? current.registrationNumber,
        },
      });
      return { kind: 'updated' };
    });
  }

  function applyTransition(e: FormEvent): void {
    e.preventDefault();
    const to = moveTo;
    if (to === null) return;
    void run(async () => {
      await callOperation('companies.company.lifecycle', {
        params: { company_uid: companyUid },
        body: transitionNeedsMergeTarget(to)
          ? { to, mergedIntoCompanyUid: mergeTargetUid }
          : { to },
      });
      return { kind: 'moved', to };
    });
  }

  function assignPortfolio(e: FormEvent): void {
    e.preventDefault();
    void run(async () => {
      await callOperation('companies.portfolio.assign', {
        params: { company_uid: companyUid },
        body: { membershipUid: portfolioMembershipUid, kind: portfolioKind },
      });
      return { kind: 'portfolioAssigned' };
    });
  }

  // ── TAB 1 · פרטי החברה ────────────────────────────────────────────────────────────────────────
  function detailsTab(current: CompanySummary): ReactNode {
  const { t } = useLocale();

    return (
      <>
        {/* WHAT IS DONE, WHAT IS ELSEWHERE AND WHAT IS NOT BUILT — the Owner's finding 6. */}
        <CompanySettingsPanel
          registrationNumber={current.registrationNumber}
          needsAttention={current.needsAttention}
          entityKind={current.entityKind}
          identityScreenHe={t('card.usersQuoted')}
        />

        <form className="form" onSubmit={(e) => saveDetails(e, current)}>
          <FieldGroup titleHe={t('card.groupBasics')} leadHe={t('card.groupBasicsLead')} defaultOpen>
            <label className="field">
              <span className="field__label">{t('card.legalName')}</span>
              <input
                className="field__input"
                name="legalName"
                value={legalName ?? current.legalName}
                onChange={(e) => setLegalName(e.currentTarget.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('onboard.displayName')}</span>
              <input
                className="field__input"
                name="displayName"
                value={displayName ?? current.displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
              />
            </label>
          </FieldGroup>

          <FieldGroup
            titleHe={t('card.groupIdentity')}
            leadHe={t('card.groupIdentityLead')}
            defaultOpen={current.registrationNumber === null || current.entityKind.trim() === ''}
          >
            <label className="field">
              <span className="field__label">{t('onboard.entityKind')}</span>
              <input
                className="field__input"
                name="entityKind"
                value={entityKind ?? current.entityKind}
                onChange={(e) => setEntityKind(e.currentTarget.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">{t('card.registration')}</span>
              <input
                className="field__input"
                name="taxId"
                dir="ltr"
                value={taxId ?? current.registrationNumber ?? ''}
                onChange={(e) => setTaxId(e.currentTarget.value)}
              />
              <span className="field__hint">
                {t('card.noNumberIsReal')}
              </span>
            </label>
          </FieldGroup>

          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              // IT NO LONGER DEPENDS ON A FIELD THE SCREEN COULD NOT READ. The guard was
              // `entityKind === ''`, and the kind arrived from the server as an undeclared field,
              // so the form started blank and the button was disabled until somebody retyped a
              // value that was already stored. The contract declares the field now.
              disabled={sending || (entityKind ?? current.entityKind).trim() === ''}
            >
              {sending ? t('common.saving') : t('card.saveDetails')}
            </button>
          </div>
        </form>

        <FactList
          facts={[
            { labelHe: t('card.legalName'), value: current.legalName },
            {
              labelHe: t('card.registration'),
              value:
                current.registrationNumber === null ? null : (
                  <span dir="ltr">{current.registrationNumber}</span>
                ),
              absentHe: t('card.noRegistration'),
            },
            { labelHe: t('company.state'), value: <LifecycleChip status={current.lifecycleStatus} /> },
          ]}
        />
      </>
    );
  }

  // ── TAB 3 · חיבור ─────────────────────────────────────────────────────────────────────────────
  //
  // The connection is NOT made here, and the tab says so rather than offering a control that would
  // have to lie. Choosing an accounting system is a statement about where the books are; reaching
  // that system needs credentials, and those live on the connections screen with their own audit.
  function connectionTab(): ReactNode {
  const { t } = useLocale();

    return (
      <>
        <FactList
          facts={[
            {
              labelHe: t('card.sourceLink'),
              value:
                bindings.length === 0 ? null : (
                  <span className="chip-row">
                    {bindings.map((b) => (
                      <span key={b.externalCompanyId} className="chip" dir="ltr">
                        {b.externalCompanyId}
                      </span>
                    ))}
                  </span>
                ),
              absentHe: t('card.noSourceLink'),
            },
          ]}
        />
        <div className="actions-row">
          <a className="btn btn--nav" href={routes.tenantSources}>
            {t('card.toSources')}
          </a>
        </div>
      </>
    );
  }

  // ── TAB 4 · הרשאות והשלמת הפעלה ───────────────────────────────────────────────────────────────
  function accessTab(current: CompanySummary): ReactNode {
  const { t } = useLocale();

    return (
      <>
        <div className="actions-row">
          {/*
            `btn--nav` AND NOT `btn--primary`. Both of these are LINKS, and the Owner's own finding
            of 2026-10-20 is that navigation must not wear the action treatment --
            `actionAffordance.test.ts` rule 2 enforces it. The grant itself is a button, on the
            screen this leads to.
          */}
          <a className="btn btn--nav" href={routes.tenantCompanyAccess}>
            {t('onboard.grantAccess')}
          </a>
          <a className="btn btn--nav" href={routes.companyHome(current.companyUid)}>
            {t('card.openForWork')}
          </a>
        </div>
        <p className="form__note">
          {t('card.openGoesThroughGate')}
        </p>
      </>
    );
  }

  // ── TAB 5 · מצב החברה ותיק עבודה ──────────────────────────────────────────────────────────────
  function lifecycleTab(current: CompanySummary): ReactNode {
  const { t } = useLocale();

    return (
      <>
        <form className="form" onSubmit={applyTransition}>
          <h3 className="form__title">{t('card.changeState')}</h3>
          <fieldset className="field field--choice">
            <legend className="field__label">{t('card.transition')}</legend>
            {COMPANY_LIFECYCLE_STATUSES.filter((s) => s !== current.lifecycleStatus).map((status) => (
              <label key={status} className="choice">
                <input
                  type="radio"
                  name="lifecycleTo"
                  value={status}
                  checked={moveTo === status}
                  onChange={() => setMoveTo(status)}
                />
                <span>{companyTransitionVerbHe(status)}</span>
              </label>
            ))}
          </fieldset>

          {moveTo !== null && transitionNeedsMergeTarget(moveTo) ? (
            <fieldset className="field field--choice">
              <legend className="field__label">{t('card.mergedInto')}</legend>
              {/* A merge cannot be expressed without a target, so the form asks rather than failing. */}
              {ctx.visibleCompanies
                .filter((c) => c.companyUid !== companyUid)
                .map((c) => (
                  <label key={c.companyUid} className="choice">
                    <input
                      type="radio"
                      name="mergedInto"
                      value={c.companyUid}
                      checked={mergeTargetUid === c.companyUid}
                      onChange={() => setMergeTargetUid(c.companyUid)}
                    />
                    <span>{c.legalName}</span>
                  </label>
                ))}
            </fieldset>
          ) : null}

          <div className="actions-row">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={
                sending ||
                moveTo === null ||
                (moveTo !== null && transitionNeedsMergeTarget(moveTo) && mergeTargetUid === '')
              }
            >
              {t('card.performTransition')}
            </button>
          </div>
          {/* No standing sentence here. **Owner, 2026-10-28:** *"אני לא רוצה לראות הסברים בתוך המערכת. המערכת צריכה להיות נקיה."* It said a state change deletes nothing and removes no permission. The confirmation on the action is where that belongs, if anywhere. */}
        </form>

        <form className="form" onSubmit={assignPortfolio}>
          <h3 className="form__title">{t('card.portfolio')}</h3>
          <FieldGroup titleHe={t('card.groupPortfolio')} leadHe={t('card.groupPortfolioLead')}>
            <label className="field">
              <span className="field__label">{t('card.partnerId')}</span>
              <input
                className="field__input"
                name="portfolioMembershipUid"
                dir="ltr"
                value={portfolioMembershipUid}
                onChange={(e) => setPortfolioMembershipUid(e.currentTarget.value)}
              />
              <span className="field__hint">
                {t('card.portfolioIsInternal')}
              </span>
            </label>
            <label className="field">
              <span className="field__label">{t('card.assignmentKind')}</span>
              <input
                className="field__input"
                name="portfolioKind"
                dir="ltr"
                value={portfolioKind}
                onChange={(e) => setPortfolioKind(e.currentTarget.value)}
              />
            </label>
            <div className="actions-row">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={sending || portfolioMembershipUid === '' || portfolioKind === ''}
              >
                {t('card.assignPortfolio')}
              </button>
            </div>
          </FieldGroup>
        </form>
      </>
    );
  }

  const tabs: readonly TabDefinition[] = [
    {
      id: 'details',
      labelHe: t('card.tabDetails'),
      outstanding: company.registrationNumber === null || company.entityKind.trim() === '',
      render: () => detailsTab(company),
    },
    {
      id: 'accounting',
      labelHe: t('card.tabAccounting'),
      render: () => <CompanyAccountingSystem companyUid={companyUid} />,
    },
    {
      /*
       * 0030/0031. WHICH REPORTS THIS CLIENT FILES, on the client's own record -- because that
       * is a property of the client, and a settings page with a company picker would make
       * "which client am I deciding about" something a person has to hold in their head while
       * the record in front of them says something else.
       */
      id: 'deadlines',
      labelHe: t('deadlines.company.title'),
      render: () => <CompanyReminderSettings companyUid={companyUid} />,
    },
    {
      /*
       * §6.11, CLOSED 2026-11-03. `companies.work_item.create` had a route and NO STARTING
       * POINT; `reachability.ts` named this screen as where it belongs. The company is in the
       * PATH and the gate's stage 4 resolves it, so there is no picker here and nothing to
       * choose wrongly — the company is the screen you are standing on.
       */
      id: 'tasks',
      labelHe: t('card.tabTasks'),
      render: () => <CompanyWorkItems companyUid={companyUid} />,
    },
    { id: 'connection', labelHe: t('card.tabConnection'), render: () => connectionTab() },
    { id: 'access', labelHe: t('card.tabAccess'), render: () => accessTab(company) },
    { id: 'lifecycle', labelHe: t('card.tabStatus'), render: () => lifecycleTab(company) },
  ];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      screenId="tenant-company"
      titleHe={t('card.title')}
      backToParent={back}
    >
      {/* THE IDENTITY, FIXED. Which client is being edited must never depend on which tab is open. */}
      <RecordHeader
        titleHe={company.displayName.trim() === '' ? company.legalName : company.displayName}
        legalNameHe={company.legalName}
        officialNumber={company.registrationNumber}
        officialNumberLabelHe={t('card.registrationShort')}
        marks={
          <>
            <LifecycleChip status={company.lifecycleStatus} />
            {company.needsAttention ? (
              <span className="chip chip--attention">{t('card.identityNeedsAttention')}</span>
            ) : null}
          </>
        }
      />

      {/*
        THE OUTCOME SITS WITH THE RECORD AND NOT INSIDE A TAB. A save made on one tab must be
        confirmed where the person can see it, and a refusal must not be reachable only by
        remembering which tab produced it.
      */}
      {outcome === null ? null : outcome.kind === 'updated' ? (
        <StateBlock
          titleHe={t('card.savedTitle')}
          bodyHe={t('card.savedBody')}
        />
      ) : outcome.kind === 'moved' ? (
        <StateBlock
          titleHe={t('card.stateChangedTitle')}
          bodyHe={`${t('card.stateNow')}: ${lifecycleStatusHe(outcome.to)}`}
        />
      ) : outcome.kind === 'portfolioAssigned' ? (
        <StateBlock
          titleHe={t('card.portfolioAssignedTitle')}
          bodyHe={t('card.portfolioBody')}
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} renderConflict={renderCompanyConflict} />
      )}

      <TabStrip name="company" tabs={tabs} ariaLabelHe={t('card.tabsAria')} />
    </AppShell>
  );
}
